import React from 'react';
import { useUIStore } from '@/stores/uiStore';
import { GraphLayout } from '@/types';

// ─── Layout labels ─────────────────────────────────────────────────────────────

const LAYOUT_OPTIONS: { value: GraphLayout; label: string }[] = [
  { value: 'fcose', label: 'Force' },
  { value: 'dagre', label: 'Hierarchy' },
  { value: 'cola', label: 'Cola' },
  { value: 'concentric', label: 'Concentric' },
  { value: 'grid', label: 'Grid' },
  { value: 'circle', label: 'Circle' },
];

// ─── GraphControls ────────────────────────────────────────────────────────────

interface GraphControlsProps {
  onToggleFilters: () => void;
  filterCount: number;
  nodeCount: number;
  edgeCount: number;
}

const GraphControls: React.FC<GraphControlsProps> = ({
  onToggleFilters,
  filterCount,
  nodeCount,
  edgeCount,
}) => {
  const graphLayout = useUIStore((s) => s.graphLayout);
  const setGraphLayout = useUIStore((s) => s.setGraphLayout);

  return (
    <>
      {/* Top-right controls */}
      <div className="absolute top-4 right-4 flex flex-col gap-2 z-10">
        {/* Layout selector */}
        <div className="bg-white dark:bg-surface-900 rounded-xl border border-surface-200 dark:border-surface-800 shadow-panel p-2">
          <p className="text-2xs font-semibold text-surface-400 uppercase tracking-wide px-1 mb-1.5">
            Layout
          </p>
          <div className="grid grid-cols-3 gap-1">
            {LAYOUT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setGraphLayout(opt.value)}
                title={opt.label}
                className={[
                  'flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg text-2xs font-medium transition-all',
                  graphLayout === opt.value
                    ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                    : 'text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-800',
                ].join(' ')}
              >
                <span className="leading-none">{opt.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Filter button */}
        <button
          type="button"
          onClick={onToggleFilters}
          className={[
            'flex items-center gap-2 px-3 py-2 rounded-xl border shadow-panel text-sm font-medium transition-all',
            filterCount > 0
              ? 'bg-primary-700 border-primary-600 text-white'
              : 'bg-white dark:bg-surface-900 border-surface-200 dark:border-surface-800 text-surface-700 dark:text-surface-300 hover:border-primary-300',
          ].join(' ')}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          Filters
          {filterCount > 0 && (
            <span className="min-w-[18px] h-[18px] px-1 text-xs font-bold rounded-full bg-accent-500 text-white flex items-center justify-center">
              {filterCount}
            </span>
          )}
        </button>
      </div>

      {/* Bottom-left stats */}
      <div className="absolute bottom-4 left-4 z-10">
        <div className="bg-white/80 dark:bg-surface-900/80 backdrop-blur-sm rounded-lg border border-surface-200 dark:border-surface-800 px-3 py-1.5 flex items-center gap-3 text-xs text-surface-500">
          <span>
            <span className="font-semibold text-surface-700 dark:text-surface-300">{nodeCount}</span> nodes
          </span>
          <span className="w-px h-3 bg-surface-300 dark:bg-surface-700" />
          <span>
            <span className="font-semibold text-surface-700 dark:text-surface-300">{edgeCount}</span> edges
          </span>
        </div>
      </div>

      {/* Bottom-right: zoom hints */}
      <div className="absolute bottom-4 right-4 z-10">
        <div className="bg-white/80 dark:bg-surface-900/80 backdrop-blur-sm rounded-lg border border-surface-200 dark:border-surface-800 px-3 py-1.5 text-2xs text-surface-400 hidden sm:block">
          Scroll to zoom · Drag to pan · Click node to highlight · Double-click to open
        </div>
      </div>
    </>
  );
};

export default GraphControls;
